/**
 * 仪表盘：今日任务 + 关键统计 + 人脉提醒（即将过生日/需联系）。
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QuickAdd } from '../components/QuickAdd';
import { Icon } from '../components/icons';
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  LoadingBlock,
  SectionTitle,
  StatCard,
} from '../components/ui';
import { useContactsDashboard } from '../hooks/useContacts';
import { useCompleteTask, useStats, useTasks } from '../hooks/useTasks';
import { useAuth } from '../stores/auth';
import { PRIORITY_LABELS, PRIORITY_STYLES, formatMinutes, relativeDay, todayStr } from '../lib/utils';

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const today = todayStr();
  const { data: stats, isLoading: statsLoading, error: statsError } = useStats();
  // 契约查询参数只有 dueBefore/dueAfter，取同一天即"今日到期"
  const { data: todayTasks, isLoading: tasksLoading, error: tasksError } = useTasks({
    dueAfter: today,
    dueBefore: today,
    pageSize: 10,
  });
  const { data: contactsDash, error: contactsError } = useContactsDashboard();
  const completeTask = useCompleteTask();
  const [quickOpen, setQuickOpen] = useState(false);

  return (
    <div className="space-y-6">
      {/* 欢迎区 */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            你好，{user?.displayName ?? user?.email}
          </h1>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{relativeDay(today)} · 今天也要追光前行</p>
        </div>
        <Button onClick={() => setQuickOpen(true)}>
          <span className="flex items-center gap-1">
            <Icon name="plus" className="h-4 w-4" />
            添加任务
          </span>
        </Button>
      </div>

      {/* 统计卡 */}
      {statsLoading ? (
        <LoadingBlock text="加载统计…" />
      ) : statsError ? (
        <ErrorBox error={statsError} />
      ) : stats ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="任务总数" value={stats.total} accent="indigo" />
          <StatCard label="已完成" value={stats.done} accent="emerald" />
          <StatCard label="已逾期" value={stats.overdue} accent="rose" />
          <StatCard
            label="完成率"
            value={`${Math.round(stats.completionRate * 100)}%`}
            sub={`专注 ${formatMinutes(stats.focusMinutesTotal)}`}
            accent="sky"
          />
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* 今日任务 */}
        <Card className="p-5">
          <SectionTitle
            title="今日任务"
            action={
              <Link to="/tasks" className="text-sm text-indigo-600 hover:underline dark:text-indigo-400">
                全部任务
              </Link>
            }
          />
          <ErrorBox error={tasksError} />
          {tasksLoading ? (
            <LoadingBlock text="加载任务…" />
          ) : (todayTasks?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="今天没有到期任务"
              description="添加一个任务，开始高效的一天"
              icon={<Icon name="check" className="mx-auto h-10 w-10" />}
              action={
                <Button size="sm" onClick={() => setQuickOpen(true)}>
                  添加任务
                </Button>
              }
            />
          ) : (
            <ul className="space-y-2">
              {(todayTasks?.items ?? []).map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-800"
                >
                  <button
                    type="button"
                    onClick={() => completeTask.mutateAsync(t.id)}
                    className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-slate-300 text-white hover:border-emerald-500 hover:bg-emerald-500 dark:border-slate-600"
                    aria-label={`完成任务 ${t.title}`}
                  >
                    <Icon name="check" className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/tasks')}
                    className="min-w-0 flex-1 truncate text-left text-sm hover:text-indigo-600 dark:hover:text-indigo-400"
                  >
                    {t.title}
                  </button>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${PRIORITY_STYLES[t.priority]}`}>
                    {PRIORITY_LABELS[t.priority]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* 人脉提醒 */}
        <Card className="p-5">
          <SectionTitle
            title="人脉提醒"
            action={
              <Link to="/contacts" className="text-sm text-indigo-600 hover:underline dark:text-indigo-400">
                人脉中心
              </Link>
            }
          />
          <ErrorBox error={contactsError} />
          {contactsDash && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">即将来临的重要日子</h4>
                {contactsDash.upcomingDates.length === 0 ? (
                  <p className="text-sm text-slate-400">暂无</p>
                ) : (
                  <ul className="space-y-1.5">
                    {contactsDash.upcomingDates.slice(0, 5).map((d) => (
                      <li key={d.id} className="flex items-center gap-2 text-sm">
                        <Icon name="gift" className="h-4 w-4 text-amber-500" />
                        <span className="flex-1 truncate">
                          {d.contact?.name ?? ''} · {d.title}
                        </span>
                        <span className="text-xs text-slate-400">{d.daysUntil} 天后</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">该联系了</h4>
                {contactsDash.needsContact.length === 0 ? (
                  <p className="text-sm text-slate-400">暂无</p>
                ) : (
                  <ul className="space-y-1.5">
                    {contactsDash.needsContact.slice(0, 5).map((c) => (
                      <li key={c.id}>
                        <Link
                          to={`/contacts/${c.id}`}
                          className="flex items-center gap-2 text-sm hover:text-indigo-600 dark:hover:text-indigo-400"
                        >
                          <Icon name="user" className="h-4 w-4 text-sky-500" />
                          <span className="flex-1 truncate">{c.name}</span>
                          {c.company && <span className="text-xs text-slate-400">{c.company}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      <QuickAdd open={quickOpen} onClose={() => setQuickOpen(false)} />
    </div>
  );
}
